/**
 * The board's columns with their priorities (3 hides under 1300 px, 2 under
 * 1100 px) and the Finance view's variations (docs/ux/pipeline.md).
 */

import { reportTypeLabel } from '@beam/core/status';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import type { MatterRow } from '@/app/types';
import { DaysCell } from '@/components/matter/DaysCell';
import { FlagMenu } from '@/components/matter/FlagMenu';
import { StatusChip } from '@/components/matter/StatusChip';
import { buttonVariants } from '@/components/ui/button';
import { isPast, longDate, shortDate } from '@/lib/format';

/** The plaintiff gate as words beside the chip, mirroring the engine's refusals. */
export function gateSubState(row: MatterRow): string | null {
  if (row.referrer.kind !== 'plaintiff') return null;
  if (row.status === 9) return 'Plaintiff: invoice first';
  if (row.status === 11) return row.paymentReceived ? 'Plaintiff: paid, release' : 'Plaintiff: awaiting payment';
  return null;
}

export function DueCell({ dueDate, now = new Date() }: { dueDate: string; now?: Date }) {
  const past = isPast(`${dueDate}T23:59:59`, now);
  const days = Math.floor((now.getTime() - new Date(`${dueDate}T00:00:00`).getTime()) / 86_400_000);
  return (
    <span data-past-due={past} title={past ? `Due ${longDate(dueDate)}, ${days} ${days === 1 ? 'day' : 'days'} ago` : `Due ${longDate(dueDate)}`} className={past ? 'tabular font-semibold text-beam-error' : 'tabular'}>
      {shortDate(dueDate)}
    </span>
  );
}

export function pipelineColumns(finance: boolean): ColumnDef<MatterRow, unknown>[] {
  const cols: ColumnDef<MatterRow, unknown>[] = [
    // Widths are border-box and include the 32 px of cell padding; the table layout is fixed.
    { id: 'due', accessorKey: 'dueDate', header: 'Due', meta: { priority: 1, width: 92 }, cell: ({ row }) => <DueCell dueDate={row.original.dueDate} /> },
    { id: 'days', accessorKey: 'daysWaiting', header: 'Days', meta: { priority: 1, width: 64, align: 'right' }, cell: ({ row }) => <DaysCell days={row.original.daysWaiting} sla={row.original.slaState} allowedDays={row.original.allowedDays} status={row.original.status} /> },
    { id: 'matter', accessorKey: 'ref', header: 'Case', meta: { priority: 1, width: 152 }, cell: ({ row }) => <span className="tabular font-semibold">{row.original.ref}</span> },
    { id: 'patient', accessorKey: 'patientAsOnLoi', header: 'Patient (as on LOI)', meta: { priority: 1 }, cell: ({ row }) => <span className="block max-w-[220px] truncate" title={row.original.patientAsOnLoi}>{row.original.patientAsOnLoi}</span> },
    { id: 'referrer', accessorFn: (r) => r.referrer.name, header: 'Referrer', meta: { priority: 3 }, cell: ({ row }) => <span className="block max-w-[200px] truncate" title={row.original.referrer.name}>{row.original.referrer.name}{row.original.referrer.kind === 'plaintiff' ? <span className="text-beam-text-muted"> (plaintiff)</span> : null}</span> },
    { id: 'doctor', accessorFn: (r) => r.doctor.name, header: 'Doctor', meta: { priority: 2, width: 150 }, cell: ({ row }) => <span className="block max-w-[150px] truncate" title={row.original.doctor.name}>{row.original.doctor.name}</span> },
  ];
  if (!finance) {
    cols.push({ id: 'type', accessorKey: 'reportType', header: 'Type', enableSorting: false, meta: { priority: 3, width: 110 }, cell: ({ row }) => <span title={reportTypeLabel(row.original.reportType, 'label')}>{reportTypeLabel(row.original.reportType)}</span> });
  }
  cols.push({
    // The only column allowed to wrap: a gate sub-state chip beside the status chip in the Finance view.
    id: 'status', accessorKey: 'status', header: 'Status', meta: { priority: 1, width: finance ? 330 : 210, className: 'whitespace-normal py-1' },
    cell: ({ row }) => <StatusChip status={row.original.status} size="sm" subState={finance ? gateSubState(row.original) : undefined} />,
    sortingFn: (a, b) => a.original.status - b.original.status,
  });
  if (!finance) {
    cols.push({ id: 'round', accessorKey: 'round', header: 'Round', enableSorting: false, meta: { priority: 3, width: 64, align: 'right' }, cell: ({ row }) => <span className="tabular">{row.original.status >= 7 && row.original.round > 0 ? row.original.round : ''}</span> });
  } else {
    cols.push({ id: 'billing', accessorKey: 'billingBand', header: 'Billing', enableSorting: false, meta: { priority: 1, width: 120 }, cell: ({ row }) => (row.original.billingBand ? <span title={row.original.billingCode ?? undefined}>{row.original.billingBand}</span> : <span className="text-beam-text-muted">Not set</span>) });
  }
  cols.push({
    id: 'flags', header: 'Flags', enableSorting: false, meta: { priority: 2, width: 130 },
    cell: ({ row }) => {
      return <FlagMenu matterRef={row.original.ref} flags={row.original.flags} />;
    },
  });
  cols.push({
    id: 'open', header: () => <span className="sr-only">Open</span>, enableSorting: false, meta: { priority: 1, width: 80, align: 'right' },
    cell: ({ row }) => (
      <Link to={`/matters/${row.original.ref}`} aria-label={`Open ${row.original.ref}`} className={buttonVariants({ variant: 'outline', size: 'default' })}>
        Open
      </Link>
    ),
  });
  return cols;
}
